import React, { useState, useEffect, useContext } from "react";
import { Navigate } from "react-router-dom";
import "../../../../App.css";
import Navbar from "../../../ui/Navbar";
import Table from "../../../ui/Table";
import Modal from "../../../ui/Modal";
import Loader from "../../../ui/Loader";
import { AuthContext } from "../../../../context/AuthContext";
import {
  swalError,
  swalSuccess,
  swalConfirm,
} from "../../../../utils/swalAlert";
import { fetchWithAuth } from "../../../../utils/api";
import Select from "react-select";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

export default function ManageStandardizedTests() {
  const [templatesData, setTemplatesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditChanged, setIsEditChanged] = useState(false);
  const [instituteOptions, setInstituteOptions] = useState([]);
  const { isAuthenticated, primaryRole } = useContext(AuthContext);

  const breadcrumbs = [
    { name: "Home", path: "/superadmin", isLast: false },
    { name: "Manage Standardized Tests", path: "/superadmin/standardized-tests", isLast: true },
  ];

  const createButtons = [
    {
      text: "Add Standardized Test",
      onClick: () => {
        setFormData({
          templateId: "",
          templateName: "",
          instituteId: null,
        });
        setSubjects({ Mathematics: [], Physics: [], Chemistry: [] });
        setNewTopic({ Mathematics: "", Physics: "", Chemistry: "" });
        setNewSubjectName("");
        setFormError("");
        setIsCreateModalOpen(true);
      },
    },
  ];

  const [formData, setFormData] = useState({
    templateId: "",
    templateName: "",
    instituteId: null,
  });

  const [editFormData, setEditFormData] = useState({
    id: "",
    templateId: "",
    templateName: "",
    instituteId: null,
  });

  const [subjects, setSubjects] = useState({
    Mathematics: [],
    Physics: [],
    Chemistry: [],
  });

  const [newTopic, setNewTopic] = useState({
    Mathematics: "",
    Physics: "",
    Chemistry: "",
  });

  const [newSubjectName, setNewSubjectName] = useState("");

  const [formError, setFormError] = useState("");

  const fetchInstitutes = async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/institute/get-all`,
        "GET"
      );
      if (response.ok) {
        const data = await response.json();
        const options = data.map(inst => ({
          label: `${inst.name} (${inst.code})`,
          value: inst._id
        }));
        setInstituteOptions(options);
      }
    } catch (err) {
      console.error("Failed to fetch institutes:", err);
    }
  };

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(
        `${serverUrl}/api/standardized-test/get-all`,
        "GET"
      );
      if (response.ok) {
        const result = await response.json();
        setTemplatesData(result.data || result);
      } else {
        throw new Error("Failed to fetch templates");
      }
    } catch (err) {
      setError(err.message);
      swalError("Error", "Failed to fetch standardized test templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInstitutes();
    fetchTemplates();
  }, []);

  const handleDelete = async (id, name) => {
    const result = await swalConfirm(
      "Delete Standardized Test Template",
      `Are you sure you want to delete "${name}"?`,
      "Yes, delete it!",
      "No, Cancel"
    );
    if (result.isConfirmed) {
      try {
        setIsDeleting(true);
        setDeletingId(id);
        const response = await fetchWithAuth(
          `${serverUrl}/api/standardized-test/delete/${id}`,
          {
            method: "DELETE",
            credentials: "include",
          }
        );
        if (response.ok) {
          swalSuccess("Deleted", "Template deleted successfully");
          fetchTemplates();
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to delete template");
        }
      } catch (err) {
        swalError("Error", err.message);
      } finally {
        setIsDeleting(false);
        setDeletingId(null);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setIsEditChanged(true);
  };

  const handleSelectChange = (selectedOption, { name }) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selectedOption,
    }));
  };

  const handleEditSelectChange = (selectedOption, { name }) => {
    setEditFormData((prev) => ({
      ...prev,
      [name]: selectedOption,
    }));
    setIsEditChanged(true);
  };

  const handleTopicInputChange = (subject, value) => {
    setNewTopic(prev => ({
      ...prev,
      [subject]: value
    }));
  };

  const addTopic = (subject) => {
    const topicText = (newTopic[subject] || "").trim();
    if (!topicText) return;
    if ((subjects[subject] || []).includes(topicText)) {
      swalError("Duplicate Topic", `"${topicText}" is already added to ${subject}`);
      return;
    }
    setSubjects(prev => ({
      ...prev,
      [subject]: [...(prev[subject] || []), topicText]
    }));
    setNewTopic(prev => ({
      ...prev,
      [subject]: ""
    }));
    setIsEditChanged(true);
  };

  const removeTopic = (subject, index) => {
    setSubjects(prev => ({
      ...prev,
      [subject]: (prev[subject] || []).filter((_, i) => i !== index)
    }));
    setIsEditChanged(true);
  };

  const addSubject = () => {
    const name = newSubjectName.trim();
    if (!name) return;
    const exists = Object.keys(subjects).some(
      (sub) => sub.toLowerCase() === name.toLowerCase()
    );
    if (exists) {
      swalError("Duplicate Subject", `"${name}" is already added.`);
      return;
    }
    setSubjects((prev) => ({
      ...prev,
      [name]: [],
    }));
    setNewTopic((prev) => ({
      ...prev,
      [name]: "",
    }));
    setNewSubjectName("");
    setIsEditChanged(true);
  };

  const removeSubject = (subjectName) => {
    setSubjects((prev) => {
      const copy = { ...prev };
      delete copy[subjectName];
      return copy;
    });
    setNewTopic((prev) => {
      const copy = { ...prev };
      delete copy[subjectName];
      return copy;
    });
    setIsEditChanged(true);
  };

  const handleTopicInputKeyDown = (e, subject) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addTopic(subject);
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!formData.templateId.trim()) {
      setFormError("Template ID is required");
      return;
    }
    if (!formData.templateName.trim()) {
      setFormError("Template Name is required");
      return;
    }

    const payloadSubjects = [];
    for (const sub of Object.keys(subjects)) {
      if (subjects[sub] && subjects[sub].length > 0) {
        payloadSubjects.push({
          name: sub,
          topics: subjects[sub],
        });
      }
    }

    if (payloadSubjects.length === 0) {
      setFormError("At least one subject must have at least one topic.");
      return;
    }

    try {
      setIsSubmitting(true);

      const payload = {
        templateId: formData.templateId.trim(),
        templateName: formData.templateName.trim(),
        instituteId: formData.instituteId ? formData.instituteId.value : null,
        subjects: payloadSubjects,
      };

      const response = await fetchWithAuth(`${serverUrl}/api/standardized-test/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        swalSuccess("Created", "Template created successfully");
        setFormData({
          templateId: "",
          templateName: "",
          instituteId: null,
        });
        setSubjects({ Mathematics: [], Physics: [], Chemistry: [] });
        setNewTopic({ Mathematics: "", Physics: "", Chemistry: "" });
        setNewSubjectName("");
        setIsCreateModalOpen(false);
        fetchTemplates();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create template");
      }
    } catch (err) {
      setFormError(err.message);
      swalError("Error", err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditTemplate = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!editFormData.templateId.trim()) {
      setFormError("Template ID is required");
      return;
    }
    if (!editFormData.templateName.trim()) {
      setFormError("Template Name is required");
      return;
    }

    const payloadSubjects = [];
    for (const sub of Object.keys(subjects)) {
      if (subjects[sub] && subjects[sub].length > 0) {
        payloadSubjects.push({
          name: sub,
          topics: subjects[sub],
        });
      }
    }

    if (payloadSubjects.length === 0) {
      setFormError("At least one subject must have at least one topic.");
      return;
    }

    try {
      setIsSubmitting(true);

      const payload = {
        templateId: editFormData.templateId.trim(),
        templateName: editFormData.templateName.trim(),
        instituteId: editFormData.instituteId ? editFormData.instituteId.value : null,
        subjects: payloadSubjects,
      };

      const response = await fetchWithAuth(`${serverUrl}/api/standardized-test/update/${editFormData.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        swalSuccess("Updated", "Template updated successfully");
        setIsEditModalOpen(false);
        fetchTemplates();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update template");
      }
    } catch (err) {
      setFormError(err.message);
      swalError("Error", err.message);
    } finally {
      setIsSubmitting(false);
      setIsEditChanged(false);
    }
  };

  const openEditModal = (template) => {
    let selectedInstitute = null;
    if (template.instituteId) {
      const id = typeof template.instituteId === 'object' ? template.instituteId._id : template.instituteId;
      selectedInstitute = instituteOptions.find((opt) => opt.value === id) || null;
    }

    setEditFormData({
      id: template._id,
      templateId: template.templateId,
      templateName: template.templateName,
      instituteId: selectedInstitute,
    });

    const initialSubjects = {};
    const initialNewTopic = {};
    if (template.subjects && template.subjects.length > 0) {
      template.subjects.forEach(sub => {
        initialSubjects[sub.name] = [...sub.topics];
        initialNewTopic[sub.name] = "";
      });
    } else {
      initialSubjects.Mathematics = [];
      initialSubjects.Physics = [];
      initialSubjects.Chemistry = [];
      initialNewTopic.Mathematics = "";
      initialNewTopic.Physics = "";
      initialNewTopic.Chemistry = "";
    }

    setSubjects(initialSubjects);
    setNewTopic(initialNewTopic);
    setNewSubjectName("");
    setIsEditChanged(false);
    setFormError("");
    setIsEditModalOpen(true);
  };

  const renderSubjectsSection = () => {
    const subjectList = Object.keys(subjects);
    return (
      <div style={{ marginTop: "20px", marginBottom: "10px" }}>
        <h4 style={{ fontSize: "0.95rem", fontWeight: "600", color: "#4b5563", marginBottom: "12px" }}>Subjects &amp; Topics</h4>
        
        {subjectList.map(sub => (
          <div key={sub} style={subjectBoxStyle}>
            <div style={{ ...subjectTitleStyle, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>{sub}</span>
              <button
                type="button"
                style={{
                  background: "none",
                  border: "none",
                  color: "#dc2626",
                  cursor: "pointer",
                  fontSize: "0.85rem",
                  fontWeight: "500",
                  padding: "2px 6px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px"
                }}
                onClick={() => removeSubject(sub)}
                title={`Delete Subject ${sub}`}
              >
                <i className="fa-solid fa-trash-can"></i> Remove Subject
              </button>
            </div>
            
            <div style={topicsWrapperStyle}>
              {(subjects[sub] || []).map((topic, index) => (
                <span key={index} style={topicTagStyle}>
                  {topic}
                  <button
                    type="button"
                    style={topicRemoveBtnStyle}
                    onClick={() => removeTopic(sub, index)}
                    title="Remove Topic"
                  >
                    <i className="fa-solid fa-xmark"></i>
                  </button>
                </span>
              ))}
              {(subjects[sub] || []).length === 0 && (
                <span style={{ fontSize: "0.85rem", color: "#9ca3af", fontStyle: "italic" }}>No topics added</span>
              )}
            </div>

            <div style={topicInputGroupStyle}>
              <input
                type="text"
                style={topicInputStyle}
                value={newTopic[sub] || ""}
                onChange={(e) => handleTopicInputChange(sub, e.target.value)}
                onKeyDown={(e) => handleTopicInputKeyDown(e, sub)}
                placeholder={`Add topic for ${sub}`}
              />
              <button
                type="button"
                style={topicAddBtnStyle}
                onClick={() => addTopic(sub)}
              >
                Add
              </button>
            </div>
          </div>
        ))}

        {subjectList.length === 0 && (
          <div style={{ textAlign: "center", padding: "16px", color: "#9ca3af", fontStyle: "italic", border: "1px dashed #d1d5db", borderRadius: "8px", marginBottom: "16px" }}>
            No subjects added yet. Add a subject below.
          </div>
        )}

        {/* Add Subject Section */}
        <div style={{
          display: "flex",
          gap: "8px",
          marginTop: "16px",
          padding: "12px",
          border: "1px solid #e5e7eb",
          borderRadius: "8px",
          background: "#f3f4f6"
        }}>
          <input
            type="text"
            placeholder="Enter new subject name (e.g. Biology)"
            value={newSubjectName}
            onChange={(e) => setNewSubjectName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addSubject();
              }
            }}
            style={{ ...topicInputStyle, background: "#ffffff" }}
          />
          <button
            type="button"
            onClick={addSubject}
            style={{
              padding: "6px 16px",
              background: "#1e3a8a",
              color: "#ffffff",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "0.85rem",
              fontWeight: "600"
            }}
          >
            Add Subject
          </button>
        </div>
      </div>
    );
  };

  const columns = [
    { key: "templateId", label: "Template ID", sortable: true },
    { key: "templateName", label: "Template Name", sortable: true },
    { 
      key: "instituteId", 
      label: "Institute", 
      sortable: true,
      render: (row) => row.instituteId?.name ? `${row.instituteId.name} (${row.instituteId.code})` : "-",
    },
    {
      key: "subjects",
      label: "Subjects (Topics)",
      sortable: false,
      render: (row) => {
        if (!row.subjects || row.subjects.length === 0) return "-";
        return row.subjects.map(s => `${s.name} (${s.topics.length})`).join(", ");
      }
    },
    {
      key: "edit",
      label: "Edit",
      sortable: false,
      render: (row) => (
        <button
          className="edit-btn"
          onClick={() => openEditModal(row)}
          title="Edit Template"
        >
          <i className="fa-solid fa-pen-to-square"></i>
        </button>
      ),
    },
    {
      key: "delete",
      label: "Delete",
      sortable: false,
      render: (row) => (
        <button
          className="delete-btn"
          onClick={() => handleDelete(row._id, row.templateName)}
          disabled={isDeleting && deletingId === row._id}
          title="Delete Template"
        >
          {isDeleting && deletingId === row._id ? (
            <i className="fa-solid fa-spinner fa-spin"></i>
          ) : (
            <i className="fa-solid fa-trash"></i>
          )}
        </button>
      ),
    },
  ];

  // Subject and topic UI configurations
  const subjectBoxStyle = {
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    padding: "12px",
    marginBottom: "16px",
    background: "#f9fafb"
  };

  const subjectTitleStyle = {
    fontWeight: "600",
    fontSize: "0.95rem",
    marginBottom: "8px",
    color: "#374151"
  };

  const topicsWrapperStyle = {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    marginBottom: "10px"
  };

  const topicTagStyle = {
    display: "inline-flex",
    alignItems: "center",
    background: "#e0f2fe",
    color: "#0369a1",
    padding: "4px 8px",
    borderRadius: "16px",
    fontSize: "0.85rem",
    fontWeight: "500",
    border: "1px solid #bae6fd"
  };

  const topicRemoveBtnStyle = {
    background: "none",
    border: "none",
    color: "#0369a1",
    marginLeft: "6px",
    cursor: "pointer",
    fontSize: "0.8rem",
    padding: "0 2px",
    display: "flex",
    alignItems: "center"
  };

  const topicInputGroupStyle = {
    display: "flex",
    gap: "8px"
  };

  const topicInputStyle = {
    flex: "1",
    padding: "6px 10px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    fontSize: "0.875rem"
  };

  const topicAddBtnStyle = {
    padding: "6px 12px",
    background: "#0f766e",
    color: "#ffffff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "0.85rem",
    fontWeight: "500"
  };

  if (!isAuthenticated || primaryRole !== "superadmin") {
    return <Navigate to="/" />;
  }

  if (error && templatesData.length === 0) {
    return (
      <div>
        <Navbar breadcrumbs={breadcrumbs} createButtons={createButtons} />
        <div className="container" style={{ marginTop: "20px" }}>
          <h2 style={{ color: "red" }}>Error: {error}</h2>
          <button onClick={() => fetchTemplates()} className="btn-submit">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar breadcrumbs={breadcrumbs} createButtons={createButtons} />
      <div className="container" style={{ marginTop: "20px" }}>
        {loading ? (
          <Loader />
        ) : templatesData.length > 0 ? (
          <Table 
            columns={columns} 
            data={templatesData} 
            caption="Standardized Test Templates" 
          />
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              color: "#666",
            }}
          >
            <p>No standardized test templates found. Create one to get started!</p>
          </div>
        )}
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Add Standardized Test"
      >
        <form onSubmit={handleCreateTemplate}>
          {formError && <div className="error-message">{formError}</div>}

          <div className="form-group">
            <label>Template ID *</label>
            <input
              type="text"
              name="templateId"
              value={formData.templateId}
              onChange={handleInputChange}
              placeholder="Enter unique template ID"
              required
            />
          </div>

          <div className="form-group">
            <label>Template Name *</label>
            <input
              type="text"
              name="templateName"
              value={formData.templateName}
              onChange={handleInputChange}
              placeholder="Enter template name"
              required
            />
          </div>

          <div className="form-group">
            <label>Institute (Optional)</label>
            <Select
              options={instituteOptions}
              value={formData.instituteId}
              onChange={handleSelectChange}
              name="instituteId"
              placeholder="Select an institute (leave empty for general)"
              isClearable
            />
          </div>

          {renderSubjectsSection()}

          <div className="modal-buttons">
            <button
              type="submit"
              className="btn-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? <Loader style={{left:0, top:0, color:'#fff'}}/> : "Create Template"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Standardized Test"
      >
        <form onSubmit={handleEditTemplate}>
          {formError && <div className="error-message">{formError}</div>}

          <div className="form-group">
            <label>Template ID *</label>
            <input
              type="text"
              name="templateId"
              value={editFormData.templateId}
              onChange={handleEditInputChange}
              placeholder="Enter unique template ID"
              required
            />
          </div>

          <div className="form-group">
            <label>Template Name *</label>
            <input
              type="text"
              name="templateName"
              value={editFormData.templateName}
              onChange={handleEditInputChange}
              placeholder="Enter template name"
              required
            />
          </div>

          <div className="form-group">
            <label>Institute (Optional)</label>
            <Select
              options={instituteOptions}
              value={editFormData.instituteId}
              onChange={handleEditSelectChange}
              name="instituteId"
              placeholder="Select an institute (leave empty for general)"
              isClearable
            />
          </div>

          {renderSubjectsSection()}

          <div className="modal-buttons">
            <button
              type="submit"
              className="btn-submit"
              disabled={isSubmitting || !isEditChanged}
            >
              {isSubmitting ? <Loader style={{left:0, top:0, color:'#fff'}}/> : "Update Template"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
